/**
 * 管理画面の枠 (ヘッダー / ユーザーメニュー / サイドバー / 通知 / 個人設定) の文言 — 日本語 (正本)。
 *
 * サイドバーの項目名は `components/layout/Sidebar.tsx` の `MENU_SECTIONS[].items[].label` と
 * 同じ文字列でなければならない (`i18n/navLabels.test.ts` が突き合わせる)。
 */
export const shellJa = {
  header: {
    mcpUpdate: '連携の更新',
    mcpUpdateTitle:
      'この端末で応答したMCP（{version}）は配信中の版（{current}）より古い版です。更新手順を開きます。',
    toggleSidebar: 'サイドバートグル',
    tagline: '資料回収ソリューション',
    switchToLight: 'ライトモードに切替',
    switchToDark: 'ダークモードに切替',
    help: 'ヘルプ',
    helpTitle: 'ヘルプ（マニュアル）',
    bugReport: '不具合報告',
    requestStatus: '対応状況',
    requestStatusTitle: '依頼・不具合の対応状況',
    userMenu: 'ユーザーメニュー',
    currentOrg: '現在の組織',
    orgNameUnset: '(組織名未設定)',
    roleTitle: 'ロール: {role}',
    preferences: '個人設定',
    logout: 'ログアウト',
  },
  language: {
    /** 切替ボタンの aria-label / title。{language} は切替先の言語名 (その言語自身の表記)。 */
    switchTo: '表示言語を {language} に切り替える',
    saveFailed: '表示言語をアカウントに保存できませんでした。このブラウザでは切り替わっています。',
  },
  nav: {
    sections: {
      caseClient: '案件・クライアント',
      documents: '書類・資料',
      payments: '入金管理',
      communication: 'コミュニケーション',
      settings: '設定',
      admin: '管理',
      platform: 'Platform',
    },
    items: {
      dashboard: 'ダッシュボード',
      clients: 'クライアント一覧',
      clientGroups: 'クライアントグループ',
      tasks: 'タスク管理',
      cases: '案件一覧',
      householdBudgets: '家計表管理',
      calendar: 'カレンダー',
      messageWorkspace: 'メッセージ対応',
      collections: '書類回収',
      vault: '資料庫',
      reports: 'レポート',
      templates: 'テンプレート',
      paymentImports: '明細取込',
      paymentImportHistory: '取込履歴',
      paymentMatches: '入金照合',
      delinquency: '未入金者',
      virtualAccounts: '仮想口座',
      receivingAccounts: '受入口座',
      chatwork: 'Chatwork',
      line: 'LINE',
      orgSettings: '組織設定',
      docMaster: '書類マスタ',
      creditorMaster: '債権者マスタ',
      messageSettings: 'メッセージ設定',
      users: 'ユーザー管理',
      audit: '監査',
      platform: 'プラットフォーム管理',
      superAdmins: 'Super Admin管理',
      announcements: 'お知らせバナー',
    },
    notEnabled: '未有効',
  },
  myTasks: {
    title: '自分のタスク',
    count: '自分の未完了タスク {count} 件',
    countOver: '自分の未完了タスク {max} 件以上',
  },
  notifications: {
    bell: '通知',
    title: '通知',
    markAllRead: 'すべて既読',
    markingAllRead: '既読処理中...',
    moreActions: 'その他の操作',
    exportHistory: '対応履歴を CSV 出力',
    exporting: '出力中…',
    rangeTabs: '通知の表示範囲',
    tabAll: 'すべて',
    tabUnread: '未読',
    filterByType: '通知の種類で絞り込む',
    allTypes: 'すべての種類',
    markedRead: '{count} 件を既読にしました',
    emptyForType: 'この種類の通知はありません',
    emptyUnread: '未読の通知はありません',
    empty: '通知はありません',
  },
  announcement: {
    dismiss: '{title} を閉じる',
  },
  preferences: {
    title: '個人設定',
    subtitle: '表示・通知の個人設定',
    theme: 'テーマ',
    themeDescription: '画面の表示テーマを選択します',
    notifications: '通知設定',
    notificationsDescription:
      '受け取る通知の種類を選択します。OFF にした通知はベルマーク・メールともに届きません。',
    notificationItems: {
      upload: 'アップロード通知',
      uploadDescription: 'クライアントが書類をアップロードした際にベルマーク・メールで通知',
      rejection: '差し戻し通知',
      rejectionDescription: '書類が差し戻された際にベルマーク・メールで通知',
      completion: '全完了通知',
      completionDescription: 'コレクションの全書類が揃った際にベルマーク・メールで通知',
      message: 'メッセージ受信通知',
      messageDescription:
        '依頼者・Chatwork・LINE からメッセージを受信した際に、ベルマーク・メールで通知',
      weeklyDigest: '週次ダイジェスト',
      weeklyDigestDescription: '毎週月曜日に担当案件のサマリーをメールで受け取る（準備中）',
    },
    inApp: 'アプリ内通知',
    inAppDescription:
      '管理画面を開いている間、新着メッセージ・通知を画面右上にポップアップ表示します。この設定はアカウント単位でサーバーに保存され、別の PC・ブラウザでも反映されます。',
    popup: 'ポップアップ通知',
    popupDescription: '新着時に画面右上にポップアップを表示します',
    sound: '通知音',
    soundDescription:
      '新着時に短い通知音を鳴らします（ブラウザの自動再生設定により鳴らない場合があります）',
    testSound: '通知音をテスト再生',
    soundUnavailable: 'このブラウザでは通知音を再生できません',
    language: '言語',
    languageDescription:
      '管理画面の表示言語を選択します。依頼者・案件のデータ (氏名・書類名・メッセージ) は翻訳されません',
    lastLogin: '最終ログイン',
    lastLoginDescription: '直近のログイン日時を表示します',
    cancel: 'キャンセル',
    save: '保存',
    saving: '保存中...',
    saved: '個人設定を保存しました',
  },
  /** 権限の確認 (`RequirePermission`)。 */
  permission: {
    noPermissionTitle: 'このページを表示する権限がありません',
    noPermissionBody:
      'ご自身のロールではこの画面にアクセスできません。管理者にお問い合わせください。',
    checkFailed: 'アクセス権限を確認できませんでした。',
    reload: '再読み込み',
  },
  /** 画面の中のエラー境界 (AppLayout の中。root の ErrorBoundary は Provider の外で日本語のまま)。 */
  errorBoundary: {
    message: '一時的に表示できません。再読み込みしてください。',
    reload: '再読み込み',
  },
} as const;
